"use client"

import { useEffect, useState } from "react"

import { useTranslations } from "next-intl"

import { LinkButton, Row, Stack, Text } from "@/components/ui"
import { CoverLink } from "@/components/ui/cover-link"
import { useScreenVisible } from "@/platform/app-mode/use-screen-visible"

interface PromoBannerProps {
  title: string
  sub: string
  cta?: string
  ctaHref?: string
  endsAt?: string
}

const pad = (value: number) => String(value).padStart(2, "0")

const timeLeftUntil = (endsAt: string) => {
  const remaining = Math.max(0, new Date(endsAt).getTime() - Date.now())
  const totalSeconds = Math.floor(remaining / 1000)
  return { expired: remaining <= 0, hours: Math.floor(totalSeconds / 3600), minutes: Math.floor((totalSeconds % 3600) / 60), seconds: totalSeconds % 60 }
}

type TimeLeft = ReturnType<typeof timeLeftUntil>

const useCountdown = (endsAt?: string) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null)
  const screenVisible = useScreenVisible()
  useEffect(() => {
    if (!endsAt) return
    setTimeLeft(timeLeftUntil(endsAt))
    if (!screenVisible) return
    const interval = setInterval(() => setTimeLeft(timeLeftUntil(endsAt)), 1000)
    return () => clearInterval(interval)
  }, [endsAt, screenVisible])
  return timeLeft
}

const Countdown = ({ timeLeft }: { timeLeft: TimeLeft }) => {
  const t = useTranslations("marketplace.promo")
  return (
    <Row gap="100" align="center" role="timer" aria-label={t("countdownAriaLabel", { hours: timeLeft.hours, minutes: timeLeft.minutes, seconds: timeLeft.seconds })}>
      {[timeLeft.hours, timeLeft.minutes, timeLeft.seconds].map((unit, index) => (
        <Row as="span" gap="100" align="center" key={index}>
          {index > 0 && (
            <Text as="span" variant="bodyLg" family="display" tone="inherit" className="leading-none font-bold opacity-40" aria-hidden>
              :
            </Text>
          )}
          <Text
            as="span"
            variant="bodyLg"
            family="display"
            tone="inherit"
            numeric
            className="rounded-4x bg-scrim ring-veil @3xl:text-heading-md min-w-8 px-200 py-150 text-center font-extrabold ring-1 @3xl:min-w-11"
          >
            {pad(unit)}
          </Text>
        </Row>
      ))}
    </Row>
  )
}

export const PromoBanner = ({ title, sub, cta, ctaHref, endsAt }: PromoBannerProps) => {
  const timeLeft = useCountdown(endsAt)

  const action = cta && (
    <LinkButton href={ctaHref} variant="inverse" size="sm" className="text-palette-on-soft lift pointer-events-auto min-w-0 shrink-0 rounded-full px-500 font-bold @3xl:px-600">
      {cta}
    </LinkButton>
  )

  // No CTA and no countdown would leave a lopsided empty right half — center the
  // message instead so a text-only banner reads as intentional.
  const hasAside = Boolean(action) || Boolean(endsAt)

  return (
    <Row
      justify={hasAside ? "between" : "center"}
      align="center"
      gap="400"
      className="press-banner rounded-12x bg-palette-grad text-fg-inverted motion-safe:animate-rise-in relative isolate overflow-hidden p-400 @3xl:px-800 @3xl:py-700"
    >
      <span className="from-fg-inverted/12 pointer-events-none absolute -top-1600 -right-800 -z-10 size-96 bg-radial to-transparent to-70%" aria-hidden />
      <CoverLink href={cta ? ctaHref : undefined} />
      <Stack gap="100" className={`pointer-events-none relative ${hasAside ? "min-w-0" : "min-w-0 items-center text-center"}`}>
        <Text as="div" variant="bodyLg" family="display" tone="inherit" balance className="@3xl:text-display-sm font-extrabold tracking-tight">
          {title}
        </Text>
        <Text as="div" variant="bodySm" family="text" tone="inherit" className="@3xl:text-body-lg opacity-90">
          {sub}
        </Text>
        {endsAt && timeLeft && !timeLeft.expired && (
          <div className="mt-250 @3xl:hidden">
            <Countdown timeLeft={timeLeft} />
          </div>
        )}
      </Stack>
      {hasAside && (
        <Row gap="400" align="center" className="pointer-events-none relative shrink-0">
          {endsAt && timeLeft && !timeLeft.expired && (
            <div className="hidden @3xl:block">
              <Countdown timeLeft={timeLeft} />
            </div>
          )}
          {action}
        </Row>
      )}
    </Row>
  )
}
